import { useEffect } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { NetworkCanvas } from "@/components/site/NetworkCanvas";
import { useLanguage } from "@/i18n/LanguageContext";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay, ease: EASE }}
            className="block"
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = ({ onNavigate }) => {
    const { t } = useLanguage();
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 50, damping: 20 });
    const sy = useSpring(my, { stiffness: 50, damping: 20 });
    const orbX = useTransform(sx, (v) => v * 30);
    const orbY = useTransform(sy, (v) => v * 20);
    const contentX = useTransform(sx, (v) => v * -8);
    const contentY = useTransform(sy, (v) => v * -6);

    useEffect(() => {
        const onMove = (e) => {
            mx.set(e.clientX / window.innerWidth - 0.5);
            my.set(e.clientY / window.innerHeight - 0.5);
        };
        window.addEventListener("mousemove", onMove);
        return () => window.removeEventListener("mousemove", onMove);
    }, [mx, my]);

    return (
        <section
            id="home"
            className="relative min-h-screen flex flex-col justify-center overflow-hidden mesh-bg"
            data-testid="hero-section"
        >
            <NetworkCanvas />

            <motion.div
                style={{ x: orbX, y: orbY }}
                className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[480px] w-[720px] rounded-full bg-cyan-400/10 blur-[120px] animate-pulse-glow"
                aria-hidden="true"
            />

            <motion.div
                style={{ x: contentX, y: contentY }}
                className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-32 pb-24"
            >
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
                    className="font-mono text-xs md:text-sm uppercase tracking-[0.3em] text-cyan-400 mb-8"
                    data-testid="hero-overline"
                >
                    {t.hero.overline}
                </motion.p>

                <h1
                    className="font-display font-black tracking-tighter leading-[1.05] text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white max-w-5xl"
                    data-testid="hero-headline"
                >
                    <MaskedLine delay={0.25}>{t.hero.line1}</MaskedLine>
                    <MaskedLine delay={0.4}>
                        <span className="text-gradient">{t.hero.line2}</span>
                    </MaskedLine>
                    <MaskedLine delay={0.55}>
                        <span className="text-gradient">{t.hero.line3}</span>
                    </MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.8, ease: EASE }}
                    className="mt-8 max-w-xl text-base md:text-lg text-zinc-400 leading-relaxed font-light"
                    data-testid="hero-subheadline"
                >
                    {t.hero.sub}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <button
                        onClick={() => onNavigate("#contact")}
                        className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-[#00D2FF] to-[#3A7BD5] px-8 py-4 font-mono text-sm uppercase tracking-[0.15em] text-[#030305] font-bold transition-transform duration-300 hover:scale-[1.03] glow-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#030305]"
                        data-testid="hero-cta-start"
                    >
                        {t.hero.ctaPrimary}
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                    <button
                        onClick={() => onNavigate("#portfolio")}
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] backdrop-blur-md px-8 py-4 font-mono text-sm uppercase tracking-[0.15em] text-zinc-200 transition-colors duration-300 hover:border-cyan-400/50 hover:text-cyan-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                        data-testid="hero-cta-work"
                    >
                        {t.hero.ctaSecondary}
                    </button>
                </motion.div>
            </motion.div>

            <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.6 }}
                onClick={() => onNavigate("#services")}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 text-zinc-500 hover:text-cyan-400 transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 rounded"
                aria-label={t.hero.scroll}
                data-testid="hero-scroll-indicator"
            >
                <span className="font-mono text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
                <span className="flex h-10 w-6 items-start justify-center rounded-full border border-current p-1.5">
                    <span className="h-2 w-1 rounded-full bg-current animate-scroll-dot" />
                </span>
                <ArrowDown className="h-3 w-3" />
            </motion.button>

            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#030305] to-transparent" aria-hidden="true" />
        </section>
    );
};
